import "./GiftPopup.less";
import {Localize} from "@dp-instant/i18n";
import {Action, lazyGet} from "@dp-instant/core";
import {IEventEmitter, TypesCore, TypesFlow} from "@dp-instant/interfaces";
import {AdResponse} from "@dp-instant/interfaces";
import {UIEvents, useInjection, visibilityEffect} from "@dp-instant/ui";
import {GameModel} from "@game/coregame/model/GameModel";
import {showAd, showRewardOrPay} from "@game/helpers";
import {TypesGame} from "@game/TypesGame";
import {ClaimButton} from "@game/ui/components/ClaimButton/ClaimButton";
import {ContinueButton} from "@game/ui/components/ContinueButton/ContinueButton";
import {NoThanksButton} from "@game/ui/components/NoThanksButton/NoThanksButton";
import {UIConstants} from "@game/ui/constants";
import cn from "classnames";
import {useState} from "preact/hooks";
import {Images} from "../images";
import {CoinsIndicator} from "@game/ui/components/CoinsIndicator/CoinsIndicator";
import {CapturingAnimated} from "@game/ui/components/Capturing/CapturingAnimated";
import {GiftType, SkinType} from "@game/coregame/settings";
import {GiftItem} from "@game/ui/popups/GiftItem";
import {apiClient} from "@game/api/ApiClient";

export type GiftPopupProps = {
    captured: number;
    total: number;
    reward?: GiftType;
    onContinue: (collected: boolean) => Promise<void> | void;
};
export const GiftPopup = (props: GiftPopupProps) => {
    const initialState = {
        adViewed: false,
        canCollect: false
    };
    let {captured, total} = props;
    const model = useInjection<GameModel>(TypesGame.model);

    const dispatcher: IEventEmitter = useInjection(TypesCore.dispatcher);
    const availableForCollection = captured >= total;

    const [invisible] = visibilityEffect(UIConstants.popup.startDelay);
    if (__SOCIAL__ === "tg") apiClient.trackFunnelEvent("gift_screen_shown");
    const [state, setState] = useState(() => initialState);

    const close = () => {
        setState(state => ({...state}));
        dispatcher.emit(UIEvents.POPUP, {id: null});
    };

    const onClaim = async () => {
        const results = model.rewardedAdsAllowed ? await showRewardOrPay(model.cookie.getVip, "reward_gift") : true;
        // const results: AdResponse = await WaitAction.sec().then(() => AdResponse.PLAYED);

        if (results) {
            if (props.reward) {
                switch (props.reward.type) {
                    case SkinType.BUILDING:
                        model.cookie.addUserBuilding(props.reward.id);
                        break;
                    case SkinType.FIGHTER:
                        model.cookie.addUserFighter(props.reward.id);
                        break;

                    case "money":
                        model.cookie.coins += model.cookie.absoluteLevelNum * 200;
                        break;
                }
                model.onShopScreenChanged();
            }
            setState(state => ({...state, adViewed: true}));
            if (__SOCIAL__ === "tg") apiClient.trackFunnelEvent("gift_claimed");
        }
    };

    const onDecline = async () => {
        if (__SOCIAL__ !== "tg") {
            await showAd();
        }
        close();
        return props.onContinue && props.onContinue(false);
    };

    const onContinue = async () => {
        close();
        if (props.onContinue) return props.onContinue(true);
        else return lazyGet<Action>(TypesFlow.LevelNext)?.run();
    };
    const capturingAnimationDelay = 1;

    return (
        <div className="popups">
            <div className={cn("coins-bar")}>
                <CoinsIndicator
                    className={cn("coins-indicator", "coins-indicator_filled")}
                    total={model.cookie.coins}
                />
            </div>

            <div className={cn("popup-gift")}>
                <div className={cn("popup", {invisible})}>
                    <div className="popup__title">{Localize.get("ui-fillbox-title", "GIFT")}</div>
                    <div className="popup__body">
                        <GiftItem
                            reward={state.adViewed ? props.reward : null}
                            value={model.cookie.absoluteLevelNum * 200}
                        />
                        <CapturingAnimated
                            from={captured - 1}
                            to={captured}
                            startDelay={capturingAnimationDelay}
                            total={total}
                            onAnimationComplete={() => {
                                if (availableForCollection) {
                                    setState({...state, canCollect: true});
                                }
                            }}
                        />

                        <div className={cn("decoration-gift", "decoration-gift_top-left")}>
                            <Images.Gift />
                        </div>
                        <div
                            className={cn(
                                "decoration-gift",
                                "decoration-gift_right-bottom",
                                "decoration-gift_small"
                            )}
                        >
                            <Images.Gift />
                        </div>
                    </div>
                    <div className={"popup__body-buttons"}>
                        <div
                            className={cn("popup__body-button", "center", {
                                invisible: state.adViewed || !state.canCollect
                            })}
                        >
                            {!state.adViewed ? (
                                <ClaimButton
                                    showIcom={model.rewardedAdsAllowed}
                                    onClick={onClaim}
                                    starsPrice={3}
                                />
                            ) : null}
                        </div>
                    </div>

                    {state.adViewed || !availableForCollection ? (
                        <ContinueButton onClick={onContinue} />
                    ) : (
                        <NoThanksButton
                            delay={
                                UIConstants.popup.noThanksButtonDelay +
                                1000 * capturingAnimationDelay
                            }
                            onClick={onDecline}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
